/**
 * Конфіг конкретного СВІТУ (бази знань) — єдине місце, що відрізняє інстанси
 * одного движка. nw = «Хроніки Альбіону» (канонічне дерево), sd = «Кодекс
 * Давокару» (дерево з контенту). Крок до мультибази (див. GLOBAL_PLATFORM_DESIGN_BRIEF).
 */
import { CATEGORY_TREE, type CatNode } from './categories.js';

export interface NavItem { key: string; label: string; to: string; gm?: boolean }
export interface CreateType { label: string; type: string; tree: string; category: string; icon: string }

export interface WorldConfig {
  brandName: string;
  brandSub: string;
  nav: NavItem[];
  /** 'canonical' — показуємо хардкоджене navTree; 'content' — будуємо дерево з API-tree контенту. */
  navSource: 'canonical' | 'content';
  navTree: CatNode[];
  createTypes: CreateType[];
}

export const WORLD: WorldConfig = {
  brandName: 'Хроніки Альбіону',
  brandSub: 'Daggerheart · вікі світу',
  nav: [
    { key: 'world', label: 'Світ', to: '/wiki' },
    { key: 'wiki', label: 'Вікі', to: '/wiki/_/World' },
    { key: 'campaigns', label: 'Кампанії', to: '/wiki/_/Campaigns' },
    { key: 'quests', label: 'Квести', to: '/wiki/_/Quests' },
    { key: 'combat', label: 'Бій', to: '/battle' },
  ],
  navSource: 'canonical',
  navTree: CATEGORY_TREE,
  createTypes: [
    { label: 'Місто / поселення', type: 'city', tree: 'World/Cities', category: 'Міста', icon: 'city' },
    { label: 'Локація', type: 'location', tree: 'World/Locations', category: 'Локації', icon: 'location' },
    { label: 'Регіон', type: 'region', tree: 'World/Regions', category: 'Регіони', icon: 'region' },
    { label: 'NPC', type: 'npc', tree: 'Characters/NPCs', category: 'NPC', icon: 'npc' },
    { label: 'Ворог (бестіарій)', type: 'adversary', tree: 'Characters/Bestiary', category: 'Бестіарій', icon: 'beast' },
    { label: 'Фракція', type: 'faction', tree: 'Factions', category: 'Фракції', icon: 'faction' },
    { label: 'Предмет / артефакт', type: 'item', tree: 'Items', category: 'Предмети', icon: 'item' },
    { label: 'Квест', type: 'quest', tree: 'Quests', category: 'Квести', icon: 'quest' },
    { label: 'Лор / легенда', type: 'lore', tree: 'Lore/Legends', category: 'Лор', icon: 'lore' },
  ],
};

/** Іконка для вузла дерева-з-контенту за ключовими словами шляху. */
export function iconForPath(path: string): string {
  const p = path.toLowerCase();
  const has = (...k: string[]): boolean => k.some((s) => p.includes(s));
  if (has('campaign', 'book', 'ep', 'кампан', 'книг')) return 'campaign';
  if (has('act', 'акт', 'quest', 'квест', 'scenario', 'сценар')) return 'quest';
  if (has('npc', 'персонаж', 'denizen', 'inhabitant', 'keeper', 'cleric', 'merchant', 'scholar', 'autorit', 'влад')) return 'npc';
  if (has('adversar', 'bestiar', 'бестіар', 'противник', 'monster', 'beast', 'spider', 'troll')) return 'beast';
  if (has('faction', 'фракц', 'clan', 'клан', 'order', 'pact', 'church', 'церкв')) return 'faction';
  if (has('item', 'artifact', 'артефакт', 'предмет', 'weapon', 'зброя', 'potion', 'elixir', 'зіл', 'еліксир', 'herb', 'equip', 'споряд')) return 'item';
  if (has('mechanic', 'механік', 'abilit', 'здібн', 'power', 'сил', 'ritual', 'ритуал', 'trait', 'рис', 'boon', 'burden', 'благ', 'тягар', 'tradition', 'традиц', 'class')) return 'ability';
  if (has('city', 'місто', 'hold', 'голд', 'yndaros', 'йондар', 'karvosti', 'карвост', 'thistle', 'тістл', 'district', 'район', 'tavern', 'таверн', 'inn', 'готел', 'shop', 'магазин')) return 'city';
  if (has('location', 'локац', 'davokar', 'давокар', 'alberetor', 'альберетор', 'place')) return 'location';
  if (has('geograph', 'геогр', 'region', 'регіон', 'environment', 'середовищ')) return 'region';
  if (has('histor', 'істор', 'event', 'подія', 'поді', 'timeline', 'хронолог')) return 'lore';
  if (has('cosmo', 'космо', 'deit', 'бог', 'божеств', 'spirit', 'дух', 'myth', 'міф')) return 'deity';
  if (has('race', 'рас', 'people', 'народ', 'culture', 'культур', 'elf', 'ельф', 'profession', 'професі', 'archetyp', 'архетип')) return 'people';
  if (has('secret', 'секрет')) return 'lore';
  if (has('lore', 'лор', 'concept', 'історичн')) return 'lore';
  if (has('world', 'світ')) return 'world';
  return 'document';
}

const CAT_CYCLE = ['cat-world', 'cat-city', 'cat-location', 'cat-people', 'cat-faction', 'cat-character', 'cat-lore', 'cat-item', 'cat-campaign', 'cat-quest', 'cat-deity', 'cat-ability'];
/** Колір категорії для top-вузла дерева-з-контенту (стабільний по індексу). */
export function catForIndex(i: number): string {
  return CAT_CYCLE[i % CAT_CYCLE.length]!;
}
